import React from 'react';

/**
 * Design Component Registry
 * 
 * This file registers all available custom design components.
 * Each component must implement the standard design interface:
 * 
 * Props:
 * - product: The product object
 * - onDesignChange: Callback function to update design data
 * - initialData: Optional initial design data (for edit mode)
 * 
 * Example:
 * function MyDesigner({ product, onDesignChange, initialData }) {
 *   const [design, setDesign] = useState(initialData || { ... });
 *   
 *   useEffect(() => {
 *     onDesignChange(design);
 *   }, [design, onDesignChange]);
 *   
 *   return <div>... your design UI ...</div>;
 * }
 */

// Lazy load design components for code splitting
const designComponents = {
    // Placeholder/Default designer - always available
    'PlaceholderDesigner': React.lazy(() => import('../components/design/PlaceholderDesigner')),

    // Heart Lamp Designer - Heart-shaped lithophane with image positioning
    'HeartLampDesigner': React.lazy(() => import('../components/design/designers/HeartLampDesigner')),

    // Add your custom design components here:
    // 'LithophaneDesigner': React.lazy(() => import('../components/design/LithophaneDesigner')),
    // 'PhotoFrameDesigner': React.lazy(() => import('../components/design/PhotoFrameDesigner')),
    // 'CustomTextDesigner': React.lazy(() => import('../components/design/CustomTextDesigner')),
};

/**
 * Get a design component by name
 * Falls back to PlaceholderDesigner if not found
 * 
 * @param {string} componentName - The registered component name
 * @returns {React.LazyExoticComponent} The lazy-loaded component
 */
export function getDesignComponent(componentName) {
    if (!componentName) {
        console.warn('No component name provided, using PlaceholderDesigner');
        return designComponents['PlaceholderDesigner'];
    }

    if (designComponents[componentName]) {
        return designComponents[componentName];
    }

    console.warn(`Design component "${componentName}" not found, using PlaceholderDesigner`);
    return designComponents['PlaceholderDesigner'];
}

/**
 * Check if a design component is registered
 * 
 * @param {string} componentName - The component name to check
 * @returns {boolean} True if the component exists
 */
export function hasDesignComponent(componentName) {
    return componentName && designComponents.hasOwnProperty(componentName);
}

/**
 * Get a list of all registered component names
 * Useful for admin UI dropdowns
 * 
 * @returns {string[]} Array of component names
 */
export function getAvailableComponents() {
    return Object.keys(designComponents);
}

export default designComponents;
